
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const STORAGE_KEY = 'lone_trs_social_data_v2';

interface SocialData {
  instagram: string;
  instagramUrl: string;
  facebook: string;
  facebookUrl: string;
  phone: string;
  email: string;
}

const defaultData: SocialData = {
  instagram: '@lone_trs',
  instagramUrl: 'https://www.instagram.com/lone_trs',
  facebook: 'LONE TRS',
  facebookUrl: 'https://facebook.com',
  phone: '(415) 888-5336',
  email: 'lonetrsllc@gmail.com',
};

function InlineEdit({
  value,
  onSave,
  className = '',
  placeholder = 'Click to edit',
}: {
  value: string;
  onSave: (v: string) => void;
  className?: string;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = () => { onSave(draft); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };

  if (editing) {
    return (
      <div className="flex items-center gap-1 w-full">
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
          placeholder={placeholder}
          className="bg-white/10 border border-electric-blue text-white px-2 py-1 rounded text-sm flex-1 outline-none"
        />
        <button onClick={commit} className="w-6 h-6 flex items-center justify-center bg-green-500 text-white rounded cursor-pointer flex-shrink-0">
          <i className="ri-check-line text-xs w-4 h-4 flex items-center justify-center"></i>
        </button>
        <button onClick={cancel} className="w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded cursor-pointer flex-shrink-0">
          <i className="ri-close-line text-xs w-4 h-4 flex items-center justify-center"></i>
        </button>
      </div>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      title="Click to edit"
      className={`cursor-pointer group inline-flex items-center gap-1 hover:bg-white/10 rounded px-1 transition-all ${className}`}
    >
      <span className="break-all">{value}</span>
      <i className="ri-pencil-line text-electric-blue text-xs opacity-0 group-hover:opacity-100 transition-opacity w-3 h-3 flex items-center justify-center flex-shrink-0"></i>
    </span>
  );
}

export default function SocialMedia() {
  const [data, setData] = useState<SocialData>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultData, ...JSON.parse(saved) } : defaultData;
    } catch { return defaultData; }
  });

  const update = (field: keyof SocialData, value: string) => {
    const next = { ...data, [field]: value };
    setData(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const socialCards = [
    {
      title: 'Instagram',
      icon: 'ri-instagram-line',
      gradient: 'from-[#f09433] via-[#e6683c] to-[#bc1888]',
      handle: data.instagram,
      handleField: 'instagram' as keyof SocialData,
      url: data.instagramUrl,
      urlField: 'instagramUrl' as keyof SocialData,
      actionLabel: 'Visit Profile',
    },
    {
      title: 'Facebook',
      icon: 'ri-facebook-fill',
      gradient: 'from-[#1877f2] to-[#0d5dbf]',
      handle: data.facebook,
      handleField: 'facebook' as keyof SocialData,
      url: data.facebookUrl,
      urlField: 'facebookUrl' as keyof SocialData,
      actionLabel: 'Visit Page',
    },
    {
      title: 'Phone',
      icon: 'ri-phone-line',
      gradient: 'from-green-500 to-emerald-600',
      handle: data.phone,
      handleField: 'phone' as keyof SocialData,
      url: `tel:${data.phone.replace(/\D/g, '')}`,
      urlField: null,
      actionLabel: 'Call Now',
    },
    {
      title: 'Email',
      icon: 'ri-mail-line',
      gradient: 'from-[#00D4FF] to-[#00a8cc]',
      handle: data.email,
      handleField: 'email' as keyof SocialData,
      url: `mailto:${data.email}`,
      urlField: null,
      actionLabel: 'Send Email',
    },
  ];

  return (
    <section className="py-32 bg-gradient-to-b from-dark-800 to-dark-900 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-electric-blue/8 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            CONNECT <span className="text-electric-blue">WITH US</span>
          </h2>
          <p className="font-inter text-white/60 text-lg mb-2">Stay updated with our latest services and offers</p>
          <p className="font-inter text-white/40 text-xs">Click any field to edit • Changes save automatically</p>
        </motion.div>

        {/* Social Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {socialCards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-electric-blue/40 transition-all duration-300"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center`}>
                  <i className={`${card.icon} text-3xl text-white w-8 h-8 flex items-center justify-center`}></i>
                </div>
                <div className="w-full space-y-2">
                  <div className="font-orbitron text-xs text-electric-blue tracking-widest">{card.title}</div>
                  <InlineEdit
                    field={card.handleField}
                    value={card.handle}
                    onSave={v => update(card.handleField, v)}
                    className="font-inter text-white text-sm font-semibold"
                    placeholder={`Enter ${card.title}`}
                  />
                  {card.urlField && (
                    <InlineEdit
                      field={card.urlField}
                      value={card.url}
                      onSave={v => update(card.urlField as keyof SocialData, v)}
                      className="font-inter text-white/50 text-xs"
                      placeholder="Enter URL"
                    />
                  )}
                  <a
                    href={card.url}
                    target={card.urlField ? '_blank' : undefined}
                    rel={card.urlField ? 'noopener noreferrer' : undefined}
                    className="inline-block mt-1 font-inter text-electric-blue text-xs hover:underline cursor-pointer whitespace-nowrap"
                  >
                    {card.actionLabel} →
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* QR Code Section — shows the real Instagram QR page */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-lg mx-auto"
        >
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 text-center hover:bg-white/10 hover:border-electric-blue/40 transition-all duration-300">
            <div className="font-orbitron text-xs text-electric-blue tracking-widest mb-6">
              SCAN TO FOLLOW US ON INSTAGRAM
            </div>

            {/* Real Instagram QR code displayed via image embed */}
            <div className="bg-white rounded-xl p-4 mb-4 inline-flex items-center justify-center">
              <img
                src="https://www.instagram.com/lone_trs/qr/"
                alt="Instagram QR Code for @lone_trs"
                className="w-52 h-52 object-contain"
                onError={e => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLImageElement).parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <div class="w-52 h-52 flex flex-col items-center justify-center gap-3">
                        <i class="ri-instagram-line text-5xl text-pink-500"></i>
                        <span class="text-gray-700 text-sm font-semibold">@lone_trs</span>
                        <a href="https://www.instagram.com/lone_trs/qr/" target="_blank" rel="noopener noreferrer" class="text-xs text-blue-600 underline">Open QR Code</a>
                      </div>`;
                  }
                }}
              />
            </div>

            <p className="font-inter text-white/70 text-sm font-semibold mb-1">@lone_trs</p>
            <a
              href="https://www.instagram.com/lone_trs/qr/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-inter text-electric-blue text-xs hover:underline cursor-pointer"
            >
              Open QR Code Page →
            </a>
            <p className="font-inter text-white/30 text-xs mt-3">
              Scan with your phone camera to follow us on Instagram
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
